/**
 * Shape Data Binding interface
 * This is stored in Yjs shape.props.dataBinding (NOT in PostgreSQL)
 */

/**
 * Branded type for ISO date strings (prevents accidental string assignment)
 */
export type ISODateString = string & { readonly __brand: 'ISODateString' };

export function toISODateString(date: Date): ISODateString {
  return date.toISOString() as ISODateString;
}

/**
 * Sync error with structured information
 */
export interface SyncError {
  readonly code:
    | 'NETWORK_ERROR'
    | 'AUTH_EXPIRED'
    | 'RATE_LIMITED'
    | 'RECORD_NOT_FOUND'
    | 'SOURCE_DISCONNECTED';
  readonly message: string;
  readonly retryable: boolean;
}

export type StaleReason =
  | 'ttl_expired'
  | 'source_modified'
  | 'manual_invalidation';

/**
 * Conflict data when local and remote changes collide.
 * Contains both sides of the conflict plus the base record for three-way comparison.
 */
export interface ConflictData {
  readonly localChanges: Record<string, unknown>;
  readonly remoteChanges: Record<string, unknown>;
  readonly baseRecord: Record<string, unknown>;
}

/**
 * Discriminated union for sync status (carries contextual data)
 */
export type SyncStatus =
  | { readonly status: 'synced'; readonly syncedAt: ISODateString }
  | { readonly status: 'pending'; readonly startedAt: ISODateString }
  | {
      readonly status: 'error';
      readonly error: SyncError;
      readonly failedAt: ISODateString;
    }
  | {
      readonly status: 'stale';
      readonly staleSince: ISODateString;
      readonly reason: StaleReason;
    }
  | {
      readonly status: 'conflict';
      readonly conflictData: ConflictData;
      readonly detectedAt: ISODateString;
    };

/**
 * Local changes tracking for commit-based sync model
 */
export interface LocalChanges {
  readonly fields: Record<string, unknown>;
  readonly changedAt: ISODateString;
  readonly changedBy: string;
}

/**
 * Shape data binding stored in Yjs
 * This is the core interface for binding shapes to external records
 *
 * Implements commit-based sync model where local changes are visible
 * to collaborators immediately via Yjs, but require explicit commit
 * to sync back to the repository.
 */
export interface ShapeDataBinding {
  readonly sourceId: string;
  readonly recordId: string; // Value from key column (e.g., "PROC-001")
  readonly cachedRecord: Record<string, unknown>;
  readonly syncState: SyncStatus;

  /**
   * Dirty tracking to prevent external sync overwriting local edits
   * Populated when user edits a bound field locally
   */
  readonly localChanges?: LocalChanges;

  /**
   * Flag for shapes created locally that don't yet exist in repository
   * When true, recordId is a temporary ID (NEW-{shapeId})
   */
  readonly isNewRecord?: boolean;

  /**
   * Version tracking for write-back conflict detection
   */
  readonly localVersion: number;
  readonly lastWrittenVersion: number;
}

/**
 * Create initial binding for a shape
 */
export function createShapeDataBinding(
  sourceId: string,
  recordId: string,
  cachedRecord: Record<string, unknown>
): ShapeDataBinding {
  const now = toISODateString(new Date());
  return {
    sourceId,
    recordId,
    cachedRecord,
    syncState: { status: 'synced', syncedAt: now },
    isNewRecord: false,
    localVersion: 1,
    lastWrittenVersion: 0,
  };
}

/**
 * Update cached record after sync
 */
export function updateBindingCache(
  binding: ShapeDataBinding,
  cachedRecord: Record<string, unknown>
): ShapeDataBinding {
  return {
    ...binding,
    cachedRecord,
    syncState: { status: 'synced', syncedAt: toISODateString(new Date()) },
  };
}

/**
 * Mark binding as stale
 */
export function markBindingStale(
  binding: ShapeDataBinding,
  reason: StaleReason
): ShapeDataBinding {
  return {
    ...binding,
    syncState: {
      status: 'stale',
      staleSince: toISODateString(new Date()),
      reason,
    },
  };
}

/**
 * Mark binding as error
 */
export function markBindingError(
  binding: ShapeDataBinding,
  error: SyncError
): ShapeDataBinding {
  return {
    ...binding,
    syncState: {
      status: 'error',
      error,
      failedAt: toISODateString(new Date()),
    },
  };
}

/**
 * Mark binding as conflict
 */
export function markBindingConflict(
  binding: ShapeDataBinding,
  localChanges: Record<string, unknown>,
  remoteChanges: Record<string, unknown>,
  baseRecord: Record<string, unknown>
): ShapeDataBinding {
  const now = toISODateString(new Date());
  return {
    ...binding,
    syncState: {
      status: 'conflict',
      conflictData: {
        localChanges,
        remoteChanges,
        baseRecord,
      },
      detectedAt: now,
    },
  };
}

/**
 * Check if binding has uncommitted local changes
 */
export function hasUncommittedChanges(binding: ShapeDataBinding): boolean {
  return (
    binding.isNewRecord === true ||
    (binding.localChanges !== undefined &&
      Object.keys(binding.localChanges.fields).length > 0)
  );
}

/**
 * Check if binding has a conflict
 */
export function hasConflict(binding: ShapeDataBinding): boolean {
  return binding.syncState.status === 'conflict';
}

/**
 * Get the effective record by merging cachedRecord with localChanges
 * This is what should be displayed to the user
 */
export function getEffectiveRecord(
  binding: ShapeDataBinding
): Record<string, unknown> {
  if (!binding.localChanges) {
    return binding.cachedRecord;
  }
  return {
    ...binding.cachedRecord,
    ...binding.localChanges.fields,
  };
}

/**
 * Create a new record binding (isNewRecord = true)
 * Used when a shape is created in a repo-connected diagram
 */
export function createNewRecordBinding(
  sourceId: string,
  shapeId: string,
  initialFields: Record<string, unknown>,
  userId: string
): ShapeDataBinding {
  const now = toISODateString(new Date());
  return {
    sourceId,
    recordId: `NEW-${shapeId}`,
    cachedRecord: {},
    syncState: { status: 'synced', syncedAt: now },
    localChanges: {
      fields: initialFields,
      changedAt: now,
      changedBy: userId,
    },
    isNewRecord: true,
    localVersion: 1,
    lastWrittenVersion: 0,
  };
}

/**
 * Apply local changes to a binding
 */
export function applyLocalChanges(
  binding: ShapeDataBinding,
  changes: Record<string, unknown>,
  userId: string
): ShapeDataBinding {
  const now = toISODateString(new Date());
  const existingFields = binding.localChanges?.fields ?? {};
  return {
    ...binding,
    localChanges: {
      fields: { ...existingFields, ...changes },
      changedAt: now,
      changedBy: userId,
    },
    localVersion: binding.localVersion + 1,
  };
}

/**
 * Discard local changes and revert to cached record
 */
export function discardLocalChanges(
  binding: ShapeDataBinding
): ShapeDataBinding {
  if (binding.isNewRecord) {
    // Cannot discard a new record - it should be deleted instead
    throw new Error('Cannot discard local changes on a new record. Delete the shape instead.');
  }
  return {
    ...binding,
    localChanges: undefined,
    syncState: { status: 'synced', syncedAt: toISODateString(new Date()) },
  };
}

/**
 * Mark binding as committed (clear local changes, update cache)
 */
export function markBindingCommitted(
  binding: ShapeDataBinding,
  committedRecord: Record<string, unknown>,
  newRecordId?: string
): ShapeDataBinding {
  const now = toISODateString(new Date());
  return {
    ...binding,
    recordId: newRecordId ?? binding.recordId,
    cachedRecord: committedRecord,
    localChanges: undefined,
    isNewRecord: false,
    syncState: { status: 'synced', syncedAt: now },
    lastWrittenVersion: binding.localVersion,
  };
}

/**
 * Resolve a conflict by choosing local, remote, or merged values
 */
export function resolveConflict(
  binding: ShapeDataBinding,
  resolution: 'local' | 'remote' | 'merge',
  mergedFields?: Record<string, unknown>
): ShapeDataBinding {
  const now = toISODateString(new Date());

  if (binding.syncState.status !== 'conflict') {
    return binding;
  }

  switch (resolution) {
    case 'local':
      // Keep local changes as-is, cachedRecord already has fresh remote data from pull
      return {
        ...binding,
        syncState: { status: 'synced', syncedAt: now },
      };

    case 'remote':
      // Accept remote data (cachedRecord already has fresh data from pull), discard local
      return {
        ...binding,
        localChanges: undefined,
        syncState: { status: 'synced', syncedAt: now },
      };

    case 'merge':
      // Use provided merged fields as new local changes
      if (!mergedFields) {
        throw new Error('mergedFields required for merge resolution');
      }
      return {
        ...binding,
        localChanges: {
          fields: mergedFields,
          changedAt: now,
          changedBy: binding.localChanges?.changedBy ?? 'system',
        },
        syncState: { status: 'synced', syncedAt: now },
      };
  }
}
